import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Icons } from "@pankod/refine-antd";
import { useCustom } from "@pankod/refine-core";
import ENV from "utils/env";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { extractPaginated } from "../utils";

type RawShop = {
  shopCode: string;
  totalEvents: string;
  productClicks: string;
  searches: string;
  fiveGChecks: string;
  compareAdds: string;
};

interface ShopMetricsTableProps {
  dateRange: [string, string];
  shopCode: string | null;
}

export const ShopMetricsTable: React.FC<ShopMetricsTableProps> = ({
  dateRange,
  shopCode,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [dateRange, shopCode]);

  const { data: response, isFetching } = useCustom({
    url: `${ENV.API_URL}/api/analytics/stats/top-shops`,
    method: "get",
    config: {
      query: {
        startDate: dateRange[0],
        endDate: dateRange[1],
        page,
        limit: pageSize,
        ...(shopCode ? { shopCode } : {}),
      },
    },
    queryOptions: { retry: 0 },
  });

  const { items, total } = extractPaginated<RawShop>(response?.data);

  const dataSource = items.map((r, i) => ({
    key: (page - 1) * pageSize + i,
    shopCode: r.shopCode,
    totalEvents: parseInt(r.totalEvents, 10),
    productClicks: parseInt(r.productClicks, 10),
    searches: parseInt(r.searches, 10),
    fiveGChecks: parseInt(r.fiveGChecks, 10),
    compareAdds: parseInt(r.compareAdds, 10),
  }));

  const columns = useMemo(
    () => [
      {
        title: "Mağaza Kodu",
        dataIndex: "shopCode",
        key: "shopCode",
        sorter: (a: any, b: any) => a.shopCode.localeCompare(b.shopCode),
      },
      {
        title: "Toplam Etkileşim",
        dataIndex: "totalEvents",
        key: "totalEvents",
        sorter: (a: any, b: any) => a.totalEvents - b.totalEvents,
        defaultSortOrder: "descend" as const,
      },
      {
        title: "Ürün Tıklama",
        dataIndex: "productClicks",
        key: "productClicks",
        sorter: (a: any, b: any) => a.productClicks - b.productClicks,
      },
      {
        title: "Arama",
        dataIndex: "searches",
        key: "searches",
        sorter: (a: any, b: any) => a.searches - b.searches,
      },
      {
        title: "5G Kontrol",
        dataIndex: "fiveGChecks",
        key: "fiveGChecks",
        sorter: (a: any, b: any) => a.fiveGChecks - b.fiveGChecks,
      },
      {
        title: "Karşılaştırma",
        dataIndex: "compareAdds",
        key: "compareAdds",
        sorter: (a: any, b: any) => a.compareAdds - b.compareAdds,
      },
    ],
    []
  );

  const handleExport = useCallback(() => {
    const ws = XLSX.utils.json_to_sheet(dataSource.map(({ key, ...rest }) => rest));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mağaza Metrikleri");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "magaza-metrikleri.xlsx");
  }, [dataSource]);

  return (
    <Card
      title="Mağaza Metrikleri"
      extra={
        <Button
          icon={<Icons.DownloadOutlined />}
          onClick={handleExport}
          disabled={dataSource.length === 0}
        >
          İndir
        </Button>
      }
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (t) => `Toplam ${t.toLocaleString("tr-TR")} mağaza`,
        }}
        size="small"
        scroll={{ x: 800 }}
      />
    </Card>
  );
};
